import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  collection, doc, query, orderBy, where,
  onSnapshot, setDoc, serverTimestamp
} from 'firebase/firestore';

const AuthorDashboardContext = createContext(null);

const LOAD_TIMEOUT_MS = 8000;

export function AuthorDashboardProvider({ children }) {
  const { user, authorId } = useAuth();

  const [authorProfile, setAuthorProfile] = useState(null);
  const [branding, setBranding] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [books, setBooks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [readers, setReaders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [posts, setPosts] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [arcRequests, setArcRequests] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [bookClubs, setBookClubs] = useState([]);
  // bookApplications: bookId → array of campaign application docs (across all of this author's campaigns)
  const [bookApplications, setBookApplications] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Track whether we've received the first profile snapshot
  const profileLoaded = useRef(false);

  const basePath = authorId ? `authors/${authorId}` : null;

  // If authorId is null, stop loading immediately — don't spin forever
  useEffect(() => {
    if (!authorId && !isLoading) return;
    if (!authorId) {
      // Give AuthContext a moment to populate authorId from token refresh
      const timeout = setTimeout(() => {
        if (!profileLoaded.current) {
          console.warn('AuthorDashboard: authorId is null after timeout — showing dashboard with empty state');
          setIsLoading(false);
        }
      }, LOAD_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
  }, [authorId, isLoading]);

  // Safety timeout — never spin longer than LOAD_TIMEOUT_MS
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!profileLoaded.current) {
        console.warn('AuthorDashboard: load timeout reached — showing dashboard with available data');
        setIsLoading(false);
      }
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, []);

  // Helper: create error handler for onSnapshot
  // critical = true for profile/branding (show warning), false for data collections (log only)
  function onListenerError(label, critical = false) {
    return (err) => {
      console.error(`AuthorDashboard: ${label} listener error:`, err.message);
      // Only show load error for permission-denied or other real errors, not missing docs
      if (critical && err.code !== 'not-found') {
        setLoadError(prev => prev || `Failed to load ${label}`);
      }
      // Don't block the dashboard — clear loading so it renders with what we have
      if (!profileLoaded.current) {
        profileLoaded.current = true;
        setIsLoading(false);
      }
    };
  }

  // Author profile — auto-create if missing (signup may have failed to write it)
  useEffect(() => {
    if (!authorId) return;
    const unsub = onSnapshot(
      doc(db, 'authors', authorId),
      async (snap) => {
        if (snap.exists()) {
          setAuthorProfile({ id: snap.id, ...snap.data() });
        } else if (user) {
          // Profile document doesn't exist — create it from Auth user data
          console.warn('AuthorDashboard: profile doc missing, auto-creating from auth data');
          try {
            await setDoc(doc(db, 'authors', authorId), {
              name: user.displayName || user.email?.split('@')[0] || 'Author',
              email: user.email || '',
              penName: '',
              genres: [],
              bookLimit: 5,
              tier: 'free',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            // onSnapshot will fire again with the new doc — no need to set state here
          } catch (err) {
            console.error('AuthorDashboard: failed to auto-create profile:', err.message);
            setLoadError('Could not create your author profile. Please try refreshing.');
          }
        }
        profileLoaded.current = true;
        setIsLoading(false);
      },
      onListenerError('author profile', true)
    );
    return unsub;
  }, [authorId, user]);

  // Branding settings — auto-create if missing
  useEffect(() => {
    if (!basePath || !authorId) return;
    const brandingRef = doc(db, 'authors', authorId, 'settings', 'branding');
    const unsub = onSnapshot(
      brandingRef,
      async (snap) => {
        if (snap.exists()) {
          setBranding(snap.data());
        } else {
          // Branding doc doesn't exist — create defaults so updateDoc never fails
          try {
            await setDoc(brandingRef, {
              accentColor: 'teal',
              logo: '',
              banner: '',
              displayName: authorProfile?.penName || authorProfile?.name || '',
              bio: '',
              website: '',
              socialLinks: {},
              updatedAt: serverTimestamp(),
            });
            // onSnapshot will fire again with the new doc
          } catch (err) {
            console.warn('AuthorDashboard: failed to auto-create branding doc:', err.message);
          }
        }
      },
      onListenerError('branding', true)
    );
    return unsub;
  }, [basePath, authorId, authorProfile]);

  // Subscription settings
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      doc(db, basePath, 'settings', 'subscription'),
      (snap) => { if (snap.exists()) setSubscription(snap.data()); },
      onListenerError('subscription')
    );
    return unsub;
  }, [basePath]);

  // Books
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'books'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setBooks(items);
      },
      onListenerError('books')
    );
    return unsub;
  }, [basePath]);

  // Campaigns
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'campaigns'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setCampaigns(items);
      },
      onListenerError('campaigns')
    );
    return unsub;
  }, [basePath]);

  // Reviews
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'bookReviews'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setReviews(items);
      },
      onListenerError('reviews')
    );
    return unsub;
  }, [basePath]);

  // Message threads (thread-based model)
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      query(collection(db, basePath, 'messages'), orderBy('updatedAt', 'desc')),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setMessages(items);
      },
      onListenerError('messages')
    );
    return unsub;
  }, [basePath]);

  // Announcements
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      query(collection(db, basePath, 'announcements'), orderBy('createdAt', 'desc')),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setAnnouncements(items);
      },
      onListenerError('announcements')
    );
    return unsub;
  }, [basePath]);

  // Posts
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      query(collection(db, basePath, 'posts'), orderBy('createdAt', 'desc')),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setPosts(items);
      },
      onListenerError('posts')
    );
    return unsub;
  }, [basePath]);

  // Rewards
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'rewards'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setRewards(items);
      },
      onListenerError('rewards')
    );
    return unsub;
  }, [basePath]);

  // Redemptions
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'redemptions'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setRedemptions(items);
      },
      onListenerError('redemptions')
    );
    return unsub;
  }, [basePath]);

  // ARC requests
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'arcRequests'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setArcRequests(items);
      },
      onListenerError('arcRequests')
    );
    return unsub;
  }, [basePath]);

  // Audiobooks
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'audiobooks'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setAudiobooks(items);
      },
      onListenerError('audiobooks')
    );
    return unsub;
  }, [basePath]);

  // Ebooks
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'ebooks'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setEbooks(items);
      },
      onListenerError('ebooks')
    );
    return unsub;
  }, [basePath]);

  // Book clubs
  useEffect(() => {
    if (!basePath) return;
    const unsub = onSnapshot(
      collection(db, basePath, 'bookClubs'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setBookClubs(items);
      },
      onListenerError('bookClubs')
    );
    return unsub;
  }, [basePath]);

  // Campaign applications — subscribe per campaign and key by bookId for
  // consumption by the readers tab and the ARC requests tab.
  useEffect(() => {
    if (!basePath || campaigns.length === 0) {
      setBookApplications({});
      return;
    }
    const unsubs = campaigns.map(campaign => {
      const appsRef = collection(db, basePath, 'campaigns', campaign.id, 'applications');
      return onSnapshot(
        appsRef,
        (snap) => {
          const apps = [];
          snap.forEach(d => apps.push({ id: d.id, ...d.data(), campaignId: campaign.id }));
          setBookApplications(prev => ({
            ...prev,
            [campaign.bookId || campaign.id]: apps,
          }));
        },
        onListenerError('campaign applications')
      );
    });
    return () => unsubs.forEach(u => u());
  }, [basePath, campaigns]);

  // Readers — tenant-scoped union of (authorId == me) and (selectedAuthorId == me).
  // Author portal must NOT read the entire global readers/ collection; that
  // is the CRM's job. Other tenant sources (per-book reader docs, applications,
  // mailing list signups) have their own listeners and are consumed separately.
  useEffect(() => {
    if (!authorId) { setReaders([]); return; }
    const byAuthorId = new Map();
    const bySelected = new Map();
    const publish = () => {
      const merged = new Map(byAuthorId);
      for (const [id, doc] of bySelected) if (!merged.has(id)) merged.set(id, doc);
      setReaders([...merged.values()]);
    };
    const unsubA = onSnapshot(
      query(collection(db, 'readers'), where('authorId', '==', authorId)),
      (snap) => {
        byAuthorId.clear();
        snap.forEach(d => byAuthorId.set(d.id, { id: d.id, ...d.data() }));
        publish();
      },
      onListenerError('readers (authorId)')
    );
    const unsubB = onSnapshot(
      query(collection(db, 'readers'), where('selectedAuthorId', '==', authorId)),
      (snap) => {
        bySelected.clear();
        snap.forEach(d => bySelected.set(d.id, { id: d.id, ...d.data() }));
        publish();
      },
      onListenerError('readers (selectedAuthorId)')
    );
    return () => { unsubA(); unsubB(); };
  }, [authorId]);

  // Message unread count
  const messageUnreadCount = messages.filter(t => t.unreadByAuthor).length;

  // Computed stats
  const approvedReviews = reviews.filter(r => r.status === 'approved');
  const approvedWithRating = approvedReviews.filter(r => r.rating);
  const stats = {
    totalReaders: readers.length,
    totalBooks: books.filter(b => b.status !== 'archived').length,
    activeCampaigns: campaigns.filter(c => ['open', 'active', 'note_interest'].includes(c.status)).length,
    pendingApplications: campaigns.reduce((sum, c) => sum + (c.pendingCount || 0), 0),
    totalReviews: approvedReviews.length,
    avgRating: approvedWithRating.length > 0
      ? (approvedWithRating.reduce((s, r) => s + Number(r.rating || 0), 0) / approvedWithRating.length).toFixed(1)
      : 'N/A',
  };

  return (
    <AuthorDashboardContext.Provider value={{
      authorProfile, branding, subscription,
      books, campaigns, readers, reviews,
      messages, messageUnreadCount, announcements, posts,
      rewards, redemptions, arcRequests, audiobooks, ebooks, bookClubs,
      bookApplications,
      stats, isLoading, loadError, basePath, authorId,
    }}>
      {children}
    </AuthorDashboardContext.Provider>
  );
}

export function useAuthorDashboard() {
  const ctx = useContext(AuthorDashboardContext);
  if (!ctx) throw new Error('useAuthorDashboard must be inside AuthorDashboardProvider');
  return ctx;
}
